/**
 * launch.ts — LAUNCH-1 launch-scope flags.
 *
 * Orbit announces a PHOTOGRAPHY product on 2026-08-28. The video
 * product (Mux delivery, timecoded proofing, 4K masters, site hero
 * video) ships mid-September. This file is the single switch that
 * decides whether the video surfaces are rendered.
 *
 * THE RULE THIS FILE ENFORCES
 * --------------------------
 * On 2026-08-28 no video surface may:
 *   - look half-finished,
 *   - be reachable in a way that dead-ends, or
 *   - be reachable in a way that spends money (Mux ingest, storage,
 *     delivery minutes) on a capability we are not selling yet.
 *
 * So every video affordance is HIDDEN — not disabled, not badged.
 * That is this codebase's existing convention for unshipped things:
 *
 *   - `src/App.tsx:551` — Workflows routes deleted, files kept, comment
 *     left saying where they went (#341 launch cut list).
 *   - `src/components/shared/AppSidebar.tsx:109` — Deliveries removed
 *     from the sidebar (#316) because "the surface is no longer
 *     user-discoverable."
 *   - `ios/.../ProjectWorkspaceView.swift:4447` — the ComingSoonCard was
 *     DELETED rather than left dormant, because "the failure mode of a
 *     coming-soon card is that adding a new tab and forgetting to wire
 *     it produces a screen that looks finished and apologetic instead
 *     of an obvious hole."
 *
 * The one place "coming this fall" is said out loud is the marketing
 * site (`src/components/marketing/ComingSoon.tsx`), which is a
 * statement, not a door. In-app we hide.
 *
 * NOT A SILENT FAILURE
 * --------------------
 * Hiding an affordance is fine. Silently swallowing a file the studio
 * explicitly handed us is not. Anywhere a photographer can still put a
 * video in front of Orbit (drag-and-drop onto a gallery, a multi-select
 * that returns a .mov), the code must say so out loud — see
 * `VIDEO_HELD_MESSAGE` below. Never drop a file without telling anyone.
 *
 * MID-SEPTEMBER
 * -------------
 * Flip `VIDEO_SHIPPED` to `true`. Everything gated on it comes back;
 * nothing was deleted. Grep `LAUNCH-1` for the full set of call sites.
 * The iOS twin of this file is
 * `ios/Orbit/Orbit/Config/LaunchFlags.swift` and must be flipped in the
 * same release.
 */

/**
 * Whether the video product is live.
 *
 * `false` for the 2026-08-28 photography launch.
 * Flip to `true` when video ships (mid-September 2026).
 */
export const VIDEO_SHIPPED = true as boolean;

/**
 * The one sentence we say when a studio hands us a video while video is
 * held back. Honest, dated only as far as we can defend, and it never
 * offers an upgrade — no plan on sale today unlocks this.
 */
export const VIDEO_HELD_MESSAGE =
  'Video delivery arrives this fall. Photos in this batch uploaded normally.';

/** Same sentence, for surfaces that need a shorter line. */
export const VIDEO_HELD_SHORT = 'Video delivery arrives this fall.';

/**
 * P0-1 — whether Orbit Business is on sale.
 *
 * WHY THIS IS THE SAME BOOLEAN AS `VIDEO_SHIPPED`
 * ----------------------------------------------
 * PLANS-3 repositioned Business around video: 4K streaming, full-res
 * client downloads, original-master hand-off. Every one of those is
 * the mid-September product. Selling the video tier six weeks before
 * video exists is the one thing the launch checklist calls out as
 * "a purchase-decision claim" (§7). So Business goes on sale WITH
 * video, and that is one flip of `VIDEO_SHIPPED`, not two flags to
 * remember. Alias, not a copy — they cannot drift.
 *
 * It also closes P0 #2 for free: `create-checkout` still holds
 * `price_REPLACE_ME_BUSINESS_*` placeholders and correctly answers
 * 503 `price_not_configured`, but no surface offers the button any
 * more, so nobody reaches the 503.
 *
 * GATE THE SALE, NOT THE PLAN
 * ---------------------------
 * Nothing here touches entitlements. `PLAN_LIMITS.orbit_business`,
 * `FEATURE_REQUIRED_PLAN`, `effective_plan_for_org`, the webhook's
 * `STRIPE_PRICE_TO_PLAN` (including `LEGACY_BUSINESS_PRICE_IDS`) and
 * the iOS `OrbitProducts.all` catalog are all untouched. An org that
 * already resolves to `orbit_business` — legacy $149.99 Stripe
 * subscriber, admin grant, or an Apple StoreKit sub — keeps every
 * feature, keeps renewing, and still sees its own tier labelled
 * "Current" wherever plans are listed. The only thing that is hidden
 * is the path that would CREATE a new Business subscription.
 *
 * MID-SEPTEMBER
 * -------------
 * Flip `VIDEO_SHIPPED` above. Before you do, the two $199.99 /
 * $1999.99 Stripe prices must exist and be pasted into
 * `create-checkout/index.ts`, `stripe-webhook/index.ts` and
 * `src/services/billing.ts` — see docs/orbit-plans.md § "Stripe setup
 * checklist". Grep `P0-1` for every call site.
 */
// LAUNCH-1 — decoupled from VIDEO_SHIPPED, and FALSE until the Stripe
// prices exist.
//
// This was `= VIDEO_SHIPPED`, so flipping video on also put Business on
// sale — while `price_REPLACE_ME_BUSINESS_MONTHLY_199_99` was still the
// literal price id in both src/services/billing.ts and
// create-checkout/index.ts. Three of the four consumers don't call
// `isPlaceholderPriceId`, so a visitor could pick Business on the
// public pricing page, sign up, and take a 503 seconds after creating
// their account. The checklist above says exactly this; the alias made
// it possible to satisfy half of it.
//
// Video and Business are separate products and now flip separately.
// When the two real Stripe prices are pasted in, set this to true.
export const BUSINESS_ON_SALE = false as boolean;

/**
 * What we say where a studio would otherwise be told to buy Business.
 * Honest, dated only as far as we can defend, and never a link —
 * there is nothing to click through to.
 */
// LAUNCH-2 — was 'Orbit Business arrives this fall, alongside video
// delivery.' `VIDEO_SHIPPED` is true and the pricing matrix now
// describes video in the present tense, so tying Business to video's
// arrival made the same page promise video as both shipped and
// forthcoming. Business is held on its own terms: the two Stripe
// prices don't exist yet (see `BUSINESS_ON_SALE`).
export const BUSINESS_HELD_MESSAGE =
  'Orbit Business arrives later this year.';

/** Same sentence, shorter, for inline nudges. */
// LAUNCH-2 — kept in step with BUSINESS_HELD_MESSAGE above, which
// dropped "alongside video delivery" and gained "later".
export const BUSINESS_HELD_SHORT = 'Orbit Business arrives later this year.';

/**
 * NOVA-KILL-1 — three Nova capabilities held back to the first software
 * update: voice ("Talk to Nova"), the creative-director Design mode
 * ("Nova Design"), and Nova-generated websites ("Nova website design").
 *
 * Same convention as VIDEO_SHIPPED: every affordance is HIDDEN (not
 * badged, not plan-gated), the files are kept, and flipping the flag in
 * the update brings the surface back untouched. Grep NOVA-KILL-1 for
 * the full set of call sites. iOS twin: LaunchFlags.swift
 * `novaVoiceShipped` / `novaDesignShipped`.
 */
export const NOVA_VOICE_SHIPPED = false as boolean;
export const NOVA_DESIGN_SHIPPED = false as boolean;
export const NOVA_SITE_BUILDER_SHIPPED = false as boolean;

/**
 * LAUNCH-EA-1 — public launch date + early-access gating.
 *
 * Deliberately a SEPARATE switch from `VIDEO_SHIPPED` above, even
 * though both land the same day. `VIDEO_SHIPPED` also unlocks real
 * money moving (Business checkout) and needs Stripe price IDs pasted
 * in first — flipping it is a manual, deliberate act with its own
 * checklist. `IS_EARLY_ACCESS` only controls the waitlist-gated
 * signup funnel (marketing "Sign up" CTAs, the /auth signup form,
 * the nav's "Early access" copy) and has no billing surface behind
 * it, so it's safe to let it flip itself.
 *
 * Evaluated at runtime (module load in the visitor's browser), not
 * baked in at build time — so the whole early-access UI reverts to
 * normal the moment someone loads the site on or after the date, with
 * no redeploy required. Grep `LAUNCH-EA-1` for every call site.
 *
 * SEPT 14 — DONE
 * --------------
 * It flipped itself at midnight Pacific; nothing to do here. Two
 * notes closed out by LAUNCH-2 on the eve of launch:
 *   • `VIDEO_SHIPPED` being `true` IS intentional — video shipped
 *     mid-September as planned, which is now. The false-until-launch
 *     prose above it describes the 2026-08-28 photography launch and
 *     is history, not instruction.
 *   • The Business Stripe prices are still `price_REPLACE_ME`, so
 *     `BUSINESS_ON_SALE` stays false. BIZ-HOLD (2026-09-26): that is
 *     now a product decision, not an open item — Business launches
 *     later this year, and the prices get created then.
 *
 * IMPORTANT, AND THE REASON LAUNCH-2 EXISTS: flipping this flag was
 * never enough on its own. The marketing page HARD-CODED its
 * pre-launch copy — the hero pill said "Launching September 14 —
 * apply for early access", the nav pill and both CTAs pointed at
 * /early-access, and the closing card said "join the waitlist" —
 * none of it reading `IS_EARLY_ACCESS`. Midnight would have passed
 * and the front door would still have been a waitlist. Every one of
 * those surfaces now reads this flag. Grep `LAUNCH-2`.
 */
export const PUBLIC_LAUNCH_DATE = new Date('2026-09-14T07:00:00Z'); // midnight PT (PDT, UTC-7)

/**
 * LAUNCH-3 — THE WAITLIST DOOR, NO LONGER ON A TIMER.
 *
 * `IS_EARLY_ACCESS` used to be `new Date() < PUBLIC_LAUNCH_DATE`, so
 * the door opened by itself at midnight Pacific on the 14th. Rennan's
 * call for the public launch is the opposite shape: the product is
 * announced as AVAILABLE, but accounts are still let in from the
 * waitlist in batches rather than all at once. A date can't express
 * that — "available" and "gated" are now two different facts, and the
 * calendar only ever knew one of them.
 *
 * So the door is a hand-operated flag. `true` = every signup CTA
 * leads to /early-access and `waitlist-guard` keeps a brand-new
 * account out unless its email is an approved entry. Flip to `false`
 * to open signup to everyone; nothing else needs to change.
 *
 * The availability COPY is deliberately not conditioned on this — see
 * AVAILABILITY_PILL below. The page says "now available" either way,
 * because it is.
 *
 * OPEN-1 (Oct 6) — OPEN. Rennan: "officially drop the waitlist, making
 * it a sign in and sign up with 7 day free trial card required". 88
 * people sat on the list and nobody came in, so anyone can now create
 * an account; setup ends at the plan step, and every plan starts with
 * the 7-day trial below, card taken up front. The day before, FREEWEEK-1
 * had swapped the card for a free week; OPEN-1 puts the card trial back
 * (migration 20261006120000_open1_card_trial.sql ended those weeks).
 */
export const WAITLIST_GATE = false as boolean;

/**
 * The gate itself. Every call site that decides where a signup CTA
 * points, or whether waitlist-guard runs, reads this. Kept under the
 * old name so App.tsx and Auth.tsx need no edit.
 */
export const IS_EARLY_ACCESS = WAITLIST_GATE;

/**
 * The hero's availability marker. LAUNCH-4 — follows the gate, so
 * opening sign-up is the one line above: "with waitlist" while it's
 * on, plain "Now available" once it's off.
 */
export const AVAILABILITY_PILL = IS_EARLY_ACCESS ? 'Now available with waitlist' : 'Now available';

/**
 * LAUNCH-EA-1 — Google/Apple sign-in on the web Auth page. Not tied
 * to IS_EARLY_ACCESS directly: OAuth goes through Lovable's
 * cloud-auth bridge and can hand back a fully-authenticated session
 * before the waitlist-invite token check ever runs (Supabase doesn't
 * expose a pre-signup hook to gate it there either). The real gate is
 * `waitlist-guard`, called right after any sign-in completes (see the
 * `user` effect in Auth.tsx) — it lets a returning user through
 * untouched and, for a brand-new account, only lets the sign-in stand
 * if that email is an approved ("invited") waitlist entry; otherwise
 * it force-adds them to the waitlist and revokes the session
 * server-side. This flag now just shows/hides the buttons themselves
 * — flip to `false` to hide them again without touching the gate.
 * Web only; the iOS app's native sign-in is a separate surface and
 * isn't affected by this flag.
 */
export const OAUTH_SIGNIN_SHIPPED = true as boolean;

/**
 * LAUNCH-2 — THE 7-DAY TRIAL ON PAID PLANS.
 *
 * Until now there was no trial anywhere in Orbit, and
 * `src/services/billing.ts` carries a long P0-1 comment explaining
 * why the old "14-day free trial. No credit card required." claim was
 * deleted rather than zeroed: nothing enforced it, and it sat above a
 * button that opens a Stripe card form. Two false claims on a payment
 * screen.
 *
 * Rennan's call for launch: a REAL 7-day trial that takes the card up
 * front, auto-charges on day 7, and makes the visitor accept the
 * terms before it starts. All three halves of that are load-bearing,
 * so all three are configured together in
 * `supabase/functions/create-checkout/index.ts`:
 *
 *   trial_period_days             `TRIAL_DAYS`, below
 *   payment_method_collection     'always' — set EXPLICITLY, because
 *                                 Stripe's default flips to
 *                                 'if_required' for a trialing
 *                                 subscription, which is exactly the
 *                                 no-card trial we are not doing
 *   trial_settings.end_behavior   cancel if the card is somehow
 *                                 missing at the end, never a silent
 *                                 unpaid subscription
 *   consent_collection            terms_of_service: 'required'
 *
 * THE FREE TIER IS NOT THE TRIAL, and the copy must never blur them.
 * `PLAN_FEATURES.free` is 'View-only dashboard access / Explore the
 * interface / No card needed to look around' — a showroom, not a
 * working account. The trial is the 7 days of a PAID plan. So:
 * "Free to look around, no card" and "7 days free on any paid plan,
 * card required" are two different sentences and both are true.
 *
 * ONE THING THIS FILE CANNOT DO. `consent_collection` requires a
 * Terms of Service URL configured in the Stripe dashboard (Settings →
 * Checkout and Payment Links → "Terms of service"). Until that is
 * set, Stripe rejects the session. Because that would break checkout
 * on launch day, `create-checkout` catches exactly that rejection,
 * retries once WITHOUT `consent_collection`, and logs it loudly — the
 * trial and the card still happen, only Stripe's own tick-box is
 * missing. The web signup form collects its own acceptance
 * regardless (see Auth.tsx, LAUNCH-2), so a visitor has agreed to the
 * terms either way. Set the URL and the retry stops firing.
 *
 * NOT DONE, AND OUT OF REACH FROM HERE: the iOS twin. StoreKit
 * introductory offers live in App Store Connect, and all six products
 * in `ios/Orbit/Orbit/Orbit.storekit` still carry
 * `"introductoryOffer": null`. The iPhone and iPad apps are held to
 * later this fall (`IOS_SHIPPED`, below), so nothing ships a
 * contradiction today — but the offer has to be added there before
 * they do, or the apps will charge on day one while the website
 * promises seven free.
 */
/**
 * LAUNCH-2b — WHETHER THE TRIAL IS ANNOUNCED.
 *
 * Separate from everything above because the trial is the one piece of
 * this launch that lives in TWO places that deploy SEPARATELY: the
 * copy ships with the frontend on a git push to Vercel, and the
 * mechanism ships with `supabase functions deploy create-checkout`.
 * A git push does not carry the edge function.
 *
 * On launch night the function deploy was blocked (macOS kills the
 * unsigned CLI binary), so the frontend would have gone out promising
 * seven free days against a production `create-checkout` that still
 * charges on day one. That is the exact false-claim-on-the-payment-
 * screen bug the P0-1 block in `src/services/billing.ts` was written
 * about, so it is a flag instead of a race.
 *
 * `false` = every trial sentence disappears and the honest pre-trial
 * copy renders in its place. Nothing is half-said and nothing reads
 * as broken; a visitor simply sees "start free" and the real terms.
 *
 * THE ORDER MATTERS, AND ONLY ONE WAY ROUND IS SAFE:
 *   1. deploy `create-checkout`
 *   2. THEN flip this to `true` and push
 * Doing it in that order means the worst case is a customer quietly
 * getting a free week nobody advertised. The reverse is a promise
 * Stripe doesn't keep. Grep `TRIAL_SHIPPED` for every call site.
 */
/**
 * LAUNCH-3 — flipped to `true` on Rennan's explicit call, with the
 * deploy ordering above understood: `create-checkout` MUST be live
 * before this reaches production, or the page promises seven free
 * days against a function that still charges on day one.
 */
export const TRIAL_SHIPPED = true as boolean;

export const TRIAL_DAYS = 7 as number;

/**
 * SIGNUP-1 — WHETHER A PLAN IS REQUIRED TO FINISH SETUP.
 *
 * `true` = the onboarding wizard will not hand out a working account
 * until the org has a subscription behind it. The plan step is back on
 * the required walk, Free is not a signup choice, and there is no skip.
 *
 * WHY THIS IS A FLAG AND NOT JUST THE NEW BEHAVIOUR
 * ------------------------------------------------
 * The gate reads a LIVE subscription lookup, and it runs for anyone
 * whose browser has no `onboarding_complete` stamp — which includes an
 * existing studio signing in on a new laptop for the first time. If
 * `check-subscription` resolves wrongly for any established account
 * (an admin grant that doesn't surface, an Apple-billed sub, a legacy
 * Stripe price that isn't in the map), that studio is locked out of
 * its own product behind a card form.
 *
 * That is a worse failure than the one this fixes, and it would arrive
 * without warning on someone else's laptop. So it gets a switch that
 * turns the whole thing off in one push, rather than a revert that has
 * to be reasoned about under pressure. Grep `SIGNUP-1` for every call
 * site.
 */
export const SIGNUP_REQUIRES_PLAN = true as boolean;

/** One sentence, everywhere the trial is offered. Card, then charge. */
export const TRIAL_MESSAGE =
  `A ${TRIAL_DAYS}-day free trial on any paid plan. Card required — it renews automatically when the trial ends, and you can cancel any time before it does.`;

/** Same promise, short enough for a button's undercaption. */
export const TRIAL_SHORT = `${TRIAL_DAYS}-day free trial, then it renews. Cancel anytime.`;

/**
 * LAUNCH-2 — whether the iPhone / iPad apps are out.
 *
 * `false` for the web launch. The App Store build is Rennan's first
 * submission and is held to later this fall, so every mobile surface
 * on the marketing site says so out loud rather than implying a
 * download exists. Same convention as `VIDEO_SHIPPED`: one flag, and
 * a statement rather than a door — there is no link to give.
 */
export const IOS_SHIPPED = false as boolean;

/**
 * LAUNCH-5 — whether "Connect Google Calendar" is offered.
 *
 * `false` until the Google OAuth app exists: google-calendar-sync needs
 * GOOGLE_OAUTH_CLIENT_ID, GOOGLE_OAUTH_CLIENT_SECRET and
 * GOOGLE_OAUTH_REDIRECT_URI (https://<app>/oauth/google), and without
 * them the button could only fail with "Couldn't start sign-in". Apple
 * Calendar is unaffected. iOS holds the same line
 * (`LaunchFlags.googleCalendarShipped`). Flip to true once the secrets
 * are set and Google has approved the calendar scopes.
 */
export const GOOGLE_CALENDAR_SHIPPED = false as boolean;

/** The one line the marketing site says about mobile. */
export const IOS_HELD_MESSAGE =
  'The iPhone and iPad apps arrive in October.';

/** Same, as a badge. */
export const IOS_HELD_SHORT = 'Coming later this year';

/**
 * LAUNCH-2 — Nova ships labelled (Beta).
 *
 * Her engine is live and unlimited on every paid plan, but voice,
 * Design mode and site generation are all held to the first update
 * (NOVA_KILL_1 above), and she is a launch-day character with no
 * production mileage on other people's businesses. Marking her rather
 * than hiding her is the right trade here — the alternative is a
 * headline feature with no acknowledgement that it is new.
 *
 * CHARACTER BIBLE, UNBROKEN: "(Beta)" is a release-stage marker, not
 * a description of what she is. Nova is still never called an AI, a
 * bot or an assistant anywhere in product or voice.
 */
export const NOVA_IS_BETA = true as boolean;

/** The parenthetical itself, so it can never be typed two ways. */
export const NOVA_BETA_LABEL = '(Beta)';

/**
 * GAL-14 — a gallery no longer has to belong to a project.
 *
 * THE COMPLAINT, VERBATIM: "in galleries it should not be required to
 * attach to a project." It isn't a UI nit. `galleries.project_id` is
 * NOT NULL, so shooting one set of photos meant inventing a client and
 * a project first — three objects before a single upload.
 *
 * WHY THIS IS A FLAG AND NOT A DELETED `if`
 * The fix is half schema. `20260915120000_gal14_standalone_galleries.sql`
 * gives galleries their own `org_id`, re-points every policy at it, and
 * only then drops the NOT NULL. Until Lovable applies that file:
 *
 *   • `project_id: null` fails with 23502 (not-null violation), and
 *   • `org_id` doesn't exist yet, so sending it fails with PGRST204
 *     — which would break EVERY gallery creation, including the
 *     ordinary attached kind that works today.
 *
 * So the flag is the interlock, and it is FALSE until the migration is
 * in. False = exactly today's behaviour: a project is required, and
 * nothing is sent that the live schema doesn't have. True = the project
 * picker is optional and each insert carries its org.
 *
 * NOW ON. The complaint is the product being wrong, not the schema
 * being late, so the UI tells the truth first: no project is required.
 * `useCreateGallery` carries the fallback for a database where the
 * migration hasn't landed yet — it retries without `org_id` on
 * PGRST204, and on a 23502 says exactly which migration is missing
 * instead of "Failed to create gallery". Set back to false if that
 * degraded path ever needs to be shut off in one push.
 */
export const STANDALONE_GALLERIES = true as boolean;

/**
 * ONB-5 — the setup flow, rebuilt.
 *
 * Twelve beats instead of four screens and two separate slide
 * sequences. /welcome still opens the experience; the old /nova-intro
 * route folds INTO the wizard as its "Meet Nova" step, so a new studio
 * meets her once instead of twice.
 *
 * The order, and what a studio may skip:
 *
 *    1 · Contact          email + phone            REQUIRED
 *    2 · Craft            business type            REQUIRED
 *    3 · Studio           business name + URL      REQUIRED
 *    4 · Import           bring your clients       skippable
 *    5 · First form       + where to embed it      skippable
 *    6 · Passport         a goal, explained        skippable
 *    7 · Meet Nova                                 REQUIRED
 *    8 · Sites (beta)                              skippable
 *    9 · Expenses                                  skippable
 *   10 · Navigation       Aperture bar or rail     REQUIRED
 *   11 · Converge         → plan → card            REQUIRED
 *   12 · Welcome          first planet → Passport
 *
 * Off, this restores the UX-3 four-step walk exactly. That is the
 * escape hatch if the long flow ever costs conversions: one line, one
 * push, and the shorter flow is back — every step below still exists
 * behind its key.
 */
export const ONB5_FLOW = true as boolean;

/**
 * ONB-5 — the dev sandbox at /dev/onboarding.
 *
 * Runs the whole flow against a fabricated studio with a step picker,
 * so the animations and the long tail of skippable screens can be
 * worked on without making an account each time. Nothing it does
 * touches Supabase.
 *
 * Gated on `import.meta.env.DEV` as well as this flag, so it cannot
 * ship to production even if someone flips it to true by accident —
 * see src/pages/dev/OnboardingSandbox.tsx.
 */
export const ONB5_DEV_SANDBOX = true as boolean;
